import React, { useState, useCallback } from 'react';
import {
    Box, Button, IconButton, Paper, Typography, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useRecentSummary } from '../hooks/useRecentSummary';

const TRIGGER_TOP = '85px';
const FLOAT_WIDTH = 380;

function formatGeneratedAt(ts: string | null): string {
    if (!ts) return '';
    const d = new Date(ts.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return ts;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${mm}/${dd} ${hh}:${mi}`;
}

export default function RecentSummaryFloat() {
    const [isOpen, setIsOpen] = useState(false);
    const { isLoading, summary, sourceCounts, generatedAt, error, generate } = useRecentSummary();

    const handleOpen = useCallback(() => setIsOpen(true), []);
    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    return (
        <>
            {/* トリガーボタン（STAFFボタンの上、同帯） */}
            {!isOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        right: 0,
                        top: TRIGGER_TOP,
                        transform: 'translateX(-10%) translateY(-50%) rotate(-90deg)',
                        transformOrigin: 'right center',
                        zIndex: 1100,
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesomeIcon sx={{ fontSize: { xs: 14, md: 18 } }} />}
                        onClick={handleOpen}
                        sx={{
                            borderRadius: '8px 8px 0 0',
                            padding: { xs: '6px 12px', md: '10px 20px' },
                            fontWeight: 800,
                            fontSize: { xs: '10px', md: '14px' },
                            boxShadow: '0 -4px 12px rgba(14,165,233,0.35)',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                            color: '#fff',
                            textTransform: 'none',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0284c7 0%, #0891b2 100%)',
                            },
                        }}
                    >
                        直近概要
                    </Button>
                </Box>
            )}

            {/* フロートウィンドウ */}
            {isOpen && (
                <Paper
                    elevation={10}
                    sx={{
                        position: 'fixed',
                        right: 16,
                        top: 80,
                        width: FLOAT_WIDTH,
                        maxWidth: 'calc(100vw - 32px)',
                        maxHeight: 'calc(100vh - 120px)',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 10500,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e0f2fe',
                    }}
                >
                    {/* ヘッダー */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                            color: '#fff',
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                            <Typography variant="subtitle2" fontWeight={800}>
                                直近概要
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* コンテンツ */}
                    <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
                        {!summary && !isLoading && !error && (
                            <Box textAlign="center" py={3}>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    本日の変更履歴とAI応答を<br />Geminiで要約します
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AutoAwesomeIcon />}
                                    onClick={generate}
                                    sx={{
                                        background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                    }}
                                >
                                    直近概要を生成
                                </Button>
                            </Box>
                        )}

                        {isLoading && (
                            <Box textAlign="center" py={4}>
                                <CircularProgress size={32} sx={{ color: '#0ea5e9' }} />
                                <Typography variant="caption" display="block" mt={1} color="text.secondary">
                                    Geminiが要約中...
                                </Typography>
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {summary && !isLoading && (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    {sourceCounts && (
                                        <Typography variant="caption" color="text.secondary">
                                            変更履歴 {sourceCounts.change_history} 件 / AIログ {sourceCounts.ai_actions} 件
                                        </Typography>
                                    )}
                                    {generatedAt && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            {formatGeneratedAt(generatedAt)} 時点
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    component="pre"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'inherit',
                                        fontSize: '13px',
                                        lineHeight: 1.7,
                                        margin: 0,
                                        color: '#0f172a',
                                    }}
                                >
                                    {summary}
                                </Box>
                                <Box mt={2} display="flex" justifyContent="flex-end">
                                    <Button
                                        size="small"
                                        startIcon={<RefreshIcon />}
                                        onClick={generate}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        再生成
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>
            )}
        </>
    );
}
